/* eslint-disable global-require */
import Axios, { AxiosError, AxiosInstance } from 'axios'
import * as Sentry from '@sentry/react'

/**
 * Assign interceptors for responses into Axios instance
 * @param {AxiosInstance} axios - Axios instance
 * @param {Function} showErrorNotifications - Callback function for handling errors
 */
export default (axios: AxiosInstance, showErrorNotifications: (error: AxiosError | Error | unknown) => void) => {
	const handleResponseError = (error: AxiosError | Error | any) => {
		if (!Axios.isCancel(error)) {
			showErrorNotifications(error)
		}

		if (!Axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { message: 'Interceptor: Non-Axios error' } })
		} else if (!error.response) {
			Sentry.captureException(error, { extra: { message: 'Interceptor: No response received' } })
		} else {
			const { status, config } = error.response

			// Log the error to Sentry for specific status codes
			const method = (config?.method || '').toUpperCase()

			if (method === 'GET') {
				// Track all GET errors except 404
				if (status !== 404) {
					Sentry.captureException(error, { extra: { message: 'Interceptor: GET error' } })
				}
			} else if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
				// For POST, PUT and PATCH, track all errors except 400 and 409
				// 400 and 409 are validation errors for forms, so we don't want to track them
				if (status !== 400 && status !== 409) {
					Sentry.captureException(error, { extra: { message: 'Interceptor: POST/PUT/PATCH error' } })
				}
			} else {
				// For all other methods, track all errors
				Sentry.captureException(error, { extra: { message: `Interceptor: Other method error` } })
			}
		}

		return Promise.reject(error)
	}

	axios.interceptors.response.use((response) => response, handleResponseError)
}
